import type { ReactNode } from 'react'
import React, { createContext, useContext, useState, useCallback } from 'react'

import Announcer from './Announcer'

interface LiveAnnouncerContextType {
  announcePolite: (message: string) => void
  announceAssertive: (message: string) => void
}

const LiveAnnouncerContext = createContext<
  LiveAnnouncerContextType | undefined
>(undefined)

interface LiveAnnouncerProps {
  children: ReactNode
}

export const useLiveAnnouncer = (): LiveAnnouncerContextType => {
  const context = useContext(LiveAnnouncerContext)

  if (!context) {
    throw new Error(
      'useLiveAnnouncer must be used within a LiveAnnouncer provider'
    )
  }

  return context
}

const LiveAnnouncer: React.FC<LiveAnnouncerProps> = ({ children }) => {
  const [announcePoliteMessage, setAnnouncePoliteMessage] = useState('')
  const [announceAssertiveMessage, setAnnounceAssertiveMessage] = useState('')

  const announcePolite = useCallback((message: string) => {
    setAnnouncePoliteMessage(message)
  }, [])

  const announceAssertive = useCallback((message: string) => {
    setAnnounceAssertiveMessage(message)
  }, [])

  const contextValue = {
    announcePolite,
    announceAssertive
  }

  return (
    <LiveAnnouncerContext.Provider value={contextValue}>
      <>
        <Announcer
          assertiveMessage={announceAssertiveMessage}
          politeMessage={announcePoliteMessage}
        />
        {children}
      </>
    </LiveAnnouncerContext.Provider>
  )
}

export default LiveAnnouncer
